/**
 * Player-facing commands for the default LuckPerms `member` group on Survival.
 * Curated from minecraft/server/scripts/configure-minecraft-permissions.sh (member block)
 * and LibreLogin cracked auth (server README). Not auto-synced.
 */

export interface MemberCommandEntry {
  /** Primary command; shown in mono. */
  command: string
  /** Optional alias or usage hint (also shown untranslated). */
  usage?: string
  /** English source string for t(). */
  description: string
}

/** Optional map landmark shown in a category (e.g. lobby portal coords). */
export interface MemberCommandLandmark {
  /** English source string for t() — short title. */
  title: string
  /** Multiverse / world name (shown untranslated). */
  world: string
  x: number
  y: number
  z: number
  /** English source string for t() — what to do there. */
  hint: string
}

export interface MemberCommandCategory {
  /** English source string for t() — category title. */
  title: string
  /** Optional note under the title (English source for t()). */
  note?: string
  /** Optional landmark callout (coords / portal tip). */
  landmark?: MemberCommandLandmark
  commands: MemberCommandEntry[]
}

export const memberCommandCategories: MemberCommandCategory[] = [
  {
    title: 'Auth (cracked clients only)',
    note: 'Premium Microsoft/Mojang accounts log in automatically. These commands are only for non-premium launchers on the auth screen.',
    commands: [
      {
        command: '/register',
        usage: '/register <password> <password>',
        description: 'Create your account the first time you join with a cracked client.',
      },
      {
        command: '/login',
        usage: '/login <password>',
        description: 'Sign in on later visits with the same username and password.',
      },
    ],
  },
  {
    title: 'Land claims',
    note: 'Claim tools protect your builds. Extra claim settings are available through the claim commands and in-game menus.',
    commands: [
      {
        command: '/claim',
        description: 'Claim the chunk you are standing in so others cannot grief it.',
      },
      {
        command: '/unclaim',
        description: 'Remove your claim on the current chunk.',
      },
      {
        command: '/claims',
        description: 'List or manage your claims.',
      },
    ],
  },
  {
    title: 'Chat',
    commands: [
      {
        command: '/msg',
        usage: '/msg <player> <message>',
        description: 'Send a private message to another player.',
      },
      {
        command: '/reply',
        usage: '/r <message>',
        description: 'Reply to the last player who messaged you.',
      },
      {
        command: '/ignore',
        usage: '/ignore <player>',
        description: 'Ignore chat and messages from a player.',
      },
      {
        command: '/list',
        description: 'See who is online.',
      },
      {
        command: '/help',
        description: 'Open Essentials help.',
      },
      {
        command: '/rules',
        description: 'Read the server rules in chat.',
      },
    ],
  },
  {
    title: 'Money & shops',
    commands: [
      {
        command: '/balance',
        usage: '/bal',
        description: 'Check your balance.',
      },
      {
        command: '/pay',
        usage: '/pay <player> <amount>',
        description: 'Send money to another player.',
      },
      {
        command: '/baltop',
        description: 'See the richest players on the server.',
      },
      {
        command: '/shop',
        description: 'Open the server shop GUI.',
      },
      {
        command: '/sell',
        description: 'Sell items through the shop system.',
      },
      {
        command: '/sellhand',
        description: 'Sell the item in your hand.',
      },
      {
        command: '/playershop',
        description: 'Manage your player chest shop (create and edit your own shop).',
      },
    ],
  },
  {
    title: 'Worlds & travel',
    note: 'Some worlds and lobby portals are reached by walking through portal regions — no command needed.',
    landmark: {
      title: 'Protected village (lobby portal)',
      world: 'main_world',
      // From Lytair F3: -1243.670 / 63.50000 / -727.752
      x: -1243,
      y: 64,
      z: -728,
      hint: 'Head to this protected village ID in main_world — it leads to the lobby.',
    },
    commands: [
      {
        command: '/mv list',
        description: 'List worlds you can travel to.',
      },
      {
        command: '/mv tp',
        usage: '/mv tp <world>',
        description: 'Teleport yourself to an allowed world.',
      },
    ],
  },
  {
    title: 'Tags',
    commands: [
      {
        command: '/tags',
        description: 'Open the cosmetic tags menu and pick a tag you have unlocked.',
      },
    ],
  },
]

export const memberCommandsIntro =
  'These are the main commands every member can use on Survival. Staff-only tools are not listed here.'

export const memberCommandsSubtitle =
  'Default member commands on Uliunai Survival — type them in-game or use tab completion where available.'
